import { useEffect, useRef, useState } from 'react';
import { supportTicketApiError, uploadSupportTicketAttachment } from './supportTicketApi';

export const ATTACHMENT_ACCEPT = '.jpg,.jpeg,.png,.webp,.mp4,.webm,.pdf,.txt';
export const ATTACHMENT_LIMIT_TEXT = 'JPG / PNG / WebP、MP4 / WebM、PDF / TXT；最多 5 个，单个 20MB，合计 50MB。';
export const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024;
export const MAX_ATTACHMENT_TOTAL = 50 * 1024 * 1024;

export function attachmentSize(size) {
  return size >= 1024 * 1024 ? `${(size / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.ceil(size / 1024))} KB`;
}

export function validateAttachmentSelection(existing, files) {
  if (existing.length + files.length > 5) return '最多添加 5 个附件，请减少选择数量。';
  const all = [...existing, ...files];
  for (const file of files) {
    if (!/\.(jpe?g|png|webp|mp4|webm|pdf|txt)$/i.test(file.name)) return `“${file.name}”类型不支持，请选择截图、录屏、PDF 或 TXT。`;
    if (!file.size || file.size > MAX_ATTACHMENT_SIZE) return `“${file.name}”不能为空，且不能超过 20MB。`;
  }
  if (all.reduce((sum, file) => sum + file.size, 0) > MAX_ATTACHMENT_TOTAL) return '附件合计不能超过 50MB，请移除部分文件。';
  const keys = all.map(file => `${file.name}:${file.size}:${file.lastModified}`);
  if (new Set(keys).size !== keys.length) return '已选择相同文件，请勿重复添加。';
  return '';
}

export function attachmentUrl(url) {
  return typeof url === 'string' && /^(https?:\/\/|\/(?!\/|\\))/i.test(url) ? url : '';
}

export function useSupportTicketAttachments(onContentChange) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const itemsRef = useRef([]);
  const changeRef = useRef(onContentChange);
  changeRef.current = onContentChange;
  const mounted = useRef(true);
  const publish = next => {
    itemsRef.current = next;
    if (mounted.current) setItems(next);
  };
  const dispose = item => { item.controller?.abort(); if (item.preview) URL.revokeObjectURL(item.preview); };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; itemsRef.current.forEach(dispose); };
  }, []);

  const upload = async item => {
    const controller = new AbortController();
    const patch = data => {
      if (mounted.current && itemsRef.current.some(current => current.id === item.id)) {
        publish(itemsRef.current.map(current => current.id === item.id ? { ...current, ...data } : current));
      }
    };
    patch({ status: 'uploading', progress: 0, error: '', controller });
    try {
      const receipt = await uploadSupportTicketAttachment(item.file, { signal: controller.signal, onProgress: progress => patch({ progress }) });
      if (!receipt?.token) throw new Error('附件上传未完成，请重试');
      patch({ status: 'ready', progress: 100, receipt, controller: null });
    } catch (failure) {
      if (!controller.signal.aborted) patch({ status: 'error', error: supportTicketApiError(failure, '上传失败，请重试'), controller: null });
    }
  };
  const select = selected => {
    const files = Array.from(selected || []);
    if (!files.length) return;
    const problem = validateAttachmentSelection(itemsRef.current.map(item => item.file), files);
    setError(problem);
    setNotice('');
    if (problem) return;
    const added = files.map(file => ({
      id: window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
      file, status: 'uploading', progress: 0,
      preview: /^image\/(jpeg|png|webp)$/.test(file.type) && URL.createObjectURL ? URL.createObjectURL(file) : '',
    }));
    publish([...itemsRef.current, ...added]);
    changeRef.current?.();
    added.forEach(upload);
  };
  const clear = () => {
    itemsRef.current.forEach(dispose);
    publish([]);
    setError('');
    setNotice('');
  };
  return {
    items, error, notice, select, clear,
    ready: items.every(item => item.status === 'ready'),
    tokens: () => itemsRef.current.map(item => ({ token: item.receipt?.token })),
    retry: item => { if (item.status === 'error') upload(item); },
    remove: item => {
      dispose(item);
      publish(itemsRef.current.filter(current => current.id !== item.id));
      setError('');
      setNotice(`已移除 ${item.file.name}`);
      changeRef.current?.();
    },
  };
}
